/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useMemo, useCallback, useRef, useEffect} from 'react'
import PropTypes from 'prop-types'
import loadable from '@loadable/component'

// Chakra Components
import {
    Box,
    Button,
    Link,
    Flex,
    AspectRatio,
    Skeleton as ChakraSkeleton,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils'
import DynamicImage from '@salesforce/retail-react-app/app/components/dynamic-image'
import Badges from '../product-badges'
import VideoControls from '../video-controls'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../utils/site-config'
import {insertVideosAtPositions} from '../../utils/product-utils'

// Lazy load ProductZoomModal - only loaded when user clicks to zoom an image
// This defers Chakra Modal components until needed
const ProductZoomModal = loadable(() => import('../product-zoom-modal'), {ssr: false})

// Constants
const VIEW_TYPE_LARGE = 'large'
const VIEW_TYPE_GIFT_CARD = 'giftCard'
const VIEW_TYPE_ZOOM = 'zoom'
const VIEW_TYPE_VIDEO = 'video'
const SKELETON_ITEMS_COUNT = 7
const MOBILE_GAP = 4 // Gap between images in mobile carousel (px)
const DESKTOP_GAP = 8 // Gap between images in desktop grid (px)
const GRID_COLUMNS = 6
const LEADING_IMAGES_COUNT = 4
const THREE_COLUMN_TAIL_COUNT = 3
export const DYNAMIC_IMAGE_MAX_WIDTH = '1280px'

// The tail switches to 3-per-row only when exactly 3 images follow the first 4.
export const hasThreeColumnTail = (imageCount, isEnabled) =>
    Boolean(isEnabled) && imageCount - LEADING_IMAGES_COUNT === THREE_COLUMN_TAIL_COUNT

export const useThreeColumnTailEnabled = () => {
    const {app: appConfig} = getConfig()
    const {site} = useMultiSite()
    return getSiteEnvVarValue(appConfig, 'PDP_GALLERY_THREE_COLUMN_ENABLED', site?.id) === 'true'
}

// Image widths config for different grid positions
// The gallery takes ~50% of viewport, then images are 50% or 33% of that
// Desktop large: ~30vw, Desktop small: ~20vw
// Mobile: full width carousel
export const IMAGE_WIDTHS = {
    // First 4 images (~30% of viewport on desktop)
    large: {base: '100vw', md: '50vw', lg: '30vw'},
    // Remaining images (~20% of viewport on desktop)
    small: {base: '100vw', md: '50vw', lg: '20vw'},
    // Mobile carousel (full width)
    mobile: {base: '100vw', md: '100vw', lg: '100vw'}
}

// Custom magnifying glass cursor using SVG data URL
const MAGNIFYING_GLASS_CURSOR = `url("data:image/svg+xml,%3Csvg width='28' height='28' viewBox='0 0 28 28' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill='rgba(255,255,255,0.85)' d='M0 0h28v28H0z'/%3E%3Cg clip-path='url(%23a)'%3E%3Cpath d='M12.33 9.833v5m-2.5-2.5h5m7.09 9.59-4.59-4.59m-5 1.667a6.667 6.667 0 1 0 0-13.334 6.667 6.667 0 0 0 0 13.334Z' stroke='%2325252B' stroke-width='1.1' stroke-linecap='square'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='a'%3E%3Cpath fill='%23fff' d='M4 4h20v20H4z'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E") 12 12, zoom-in`

/**
 * Skeleton loading component for image gallery
 * Displays placeholder grid while images are loading
 */
export const Skeleton = () => {
    const isThreeColumnEnabled = useThreeColumnTailEnabled()
    const threeColumnTail = hasThreeColumnTail(SKELETON_ITEMS_COUNT, isThreeColumnEnabled)

    return (
        <Box data-testid="sf-image-gallery-skeleton">
            <Box
                display="grid"
                gridTemplateColumns={`repeat(${GRID_COLUMNS}, 1fr)`}
                gap={`${DESKTOP_GAP}px`}
            >
                {Array.from({length: SKELETON_ITEMS_COUNT}, (_, index) => (
                    <Box
                        key={index}
                        gridColumn={
                            threeColumnTail && index >= LEADING_IMAGES_COUNT ? 'span 2' : 'span 3'
                        }
                    >
                        <AspectRatio ratio={2 / 3}>
                            <ChakraSkeleton />
                        </AspectRatio>
                    </Box>
                ))}
            </Box>
        </Box>
    )
}

Skeleton.propTypes = {
    size: PropTypes.string
}

/**
 * Individual image component with click handling and loading optimization
 * @param {boolean} isAboveFold - Whether image is above the fold (LCP candidate)
 * @param {Object} widths - Responsive widths config for DynamicImage
 */
const ImageItem = ({
    image,
    index,
    onImageClick,
    cursor,
    alt,
    loadingStrategy,
    getImageUrl,
    isAboveFold = false,
    widths = IMAGE_WIDTHS.large,
    imageProps = {},
    imageContainerProps = {}
}) => {
    return (
        <AspectRatio ratio={2 / 3} {...imageContainerProps}>
            <DynamicImage
                src={getImageUrl(image)}
                widths={widths}
                imageProps={{
                    alt: alt || image.alt,
                    loading: loadingStrategy,
                    decoding: isAboveFold ? 'sync' : 'async',
                    fetchpriority: isAboveFold ? 'high' : 'auto',
                    onClick: () => onImageClick?.(index),
                    style: {cursor, objectFit: 'cover'},
                    ...imageProps
                }}
            />
        </AspectRatio>
    )
}

ImageItem.propTypes = {
    image: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
    onImageClick: PropTypes.func,
    cursor: PropTypes.string,
    alt: PropTypes.string,
    loadingStrategy: PropTypes.oneOf(['lazy', 'eager']).isRequired,
    getImageUrl: PropTypes.func.isRequired,
    isAboveFold: PropTypes.bool,
    widths: PropTypes.object,
    imageProps: PropTypes.object,
    imageContainerProps: PropTypes.object
}

/**
 * Video item that lazily loads and plays only when visible in viewport (desktop)
 * or when it is the active carousel slide (mobile).
 */
const VideoGalleryItem = ({image, isActiveSlide, wrapperStyle, elementStyle, cursor, onOpenModal}) => {
    const videoRef = useRef(null)
    const [isInViewport, setIsInViewport] = useState(false)

    // Intersection observer drives playback on desktop grid
    useEffect(() => {
        const video = videoRef.current
        if (!video) return
        const observer = new IntersectionObserver(
            ([entry]) => setIsInViewport(entry.isIntersecting),
            {threshold: 0.5}
        )
        observer.observe(video)
        return () => observer.disconnect()
    }, [])

    // Play when active slide (mobile) OR in viewport (desktop)
    const shouldPlay = isActiveSlide || isInViewport
    // Ref mirrors current shouldPlay so the onCanPlay handler avoids stale closures
    const shouldPlayRef = useRef(shouldPlay)
    useEffect(() => {
        shouldPlayRef.current = shouldPlay
    }, [shouldPlay])

    const [isMuted, setIsMuted] = useState(true)
    const [isPlaying, setIsPlaying] = useState(true)

    useEffect(() => {
        const video = videoRef.current
        if (!video) return
        // Set both the DOM property AND the HTML attribute:
        // - DOM property (video.muted) is checked by Chrome/Firefox at play() time
        // - HTML attribute (setAttribute) is checked by Safari for autoplay policy
        // Both must be set because React's reconciliation may reset the DOM property
        // between our imperative set and when the browser evaluates the async play() call.
        video.muted = isMuted
        if (isMuted) {
            video.setAttribute('muted', '')
        } else {
            video.removeAttribute('muted')
        }
        if (shouldPlay) {
            video.play().catch(() => {})
        } else {
            video.pause()
        }
    }, [shouldPlay, isMuted])

    // Retry play() once the browser signals it has enough data to start.
    // Guards against the race condition where play() is called before buffering begins.
    const handleCanPlay = useCallback(() => {
        const video = videoRef.current
        if (!video || !shouldPlayRef.current || !video.paused) return
        video.play().catch(() => {})
    }, [])

    const togglePlayPause = useCallback((e) => {
        e.preventDefault()
        e.stopPropagation()
        const video = videoRef.current
        if (!video) return
        if (video.paused) {
            video.play().catch(() => {})
        } else {
            video.pause()
        }
    }, [])

    const toggleMute = useCallback(
        (e) => {
            e.preventDefault()
            e.stopPropagation()
            const nextMuted = !isMuted
            const video = videoRef.current
            if (video) {
                video.muted = nextMuted
                if (nextMuted) video.setAttribute('muted', '')
                else video.removeAttribute('muted')
            }
            setIsMuted(nextMuted)
        },
        [isMuted]
    )

    return (
        <Box {...wrapperStyle} cursor={cursor} onClick={onOpenModal} data-video-hover-group>
            {/* Use a native <video> element — Chakra's <Box as="video"> can silently drop
                src/muted/loop/playsInline when those props conflict with its style system.
                autoPlay + muted is the browser-native muted-autoplay path (always allowed
                by browser autoplay policy) and matches the existing working Video component. */}
            <video
                ref={videoRef}
                autoPlay
                muted={isMuted}
                loop
                playsInline
                preload="none"
                aria-label={image.alt || 'Product video'}
                onCanPlay={handleCanPlay}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                style={elementStyle}
            >
                <source src={image.src} type="video/mp4" />
            </video>
            <VideoControls
                isPlaying={isPlaying}
                isMuted={isMuted}
                onTogglePlay={togglePlayPause}
                onToggleMute={toggleMute}
                revealOnHover
            />
        </Box>
    )
}

VideoGalleryItem.propTypes = {
    image: PropTypes.shape({
        src: PropTypes.string.isRequired,
        alt: PropTypes.string
    }).isRequired,
    isActiveSlide: PropTypes.bool,
    wrapperStyle: PropTypes.object,
    elementStyle: PropTypes.object,
    cursor: PropTypes.string,
    onOpenModal: PropTypes.func
}

/**
 * Responsive gallery component - single DOM structure
 * Mobile (base to lg): horizontal scroll carousel with custom scrollbar
 * Desktop (lg+): CSS grid of 6 columns, 2 images per row; with threeColumnTail the
 * images after the first 4 switch to 3 per row
 */
const ResponsiveGallery = ({
    images,
    badges,
    onImageClick,
    onSlideChange,
    getImageUrl,
    loadingStrategy,
    imageContainerProps,
    imageProps,
    videoItemStyle,
    videoElementStyle,
    threeColumnTail = false
}) => {
    const scrollContainerRef = useRef(null)
    const [scrollProgress, setScrollProgress] = useState(0)
    const [activeIndex, setActiveIndex] = useState(0)

    // Update scroll progress and active index (mobile only)
    const updateScrollState = useCallback(() => {
        const container = scrollContainerRef.current
        if (!container) return

        const {scrollLeft, scrollWidth, clientWidth} = container
        const maxScroll = scrollWidth - clientWidth

        // Calculate scroll progress (0 to 1)
        const progress = maxScroll > 0 ? scrollLeft / maxScroll : 0
        setScrollProgress(progress)

        // Calculate active slide index
        const slideWidth = clientWidth + MOBILE_GAP
        const newActiveIndex = Math.round(scrollLeft / slideWidth)

        if (newActiveIndex !== activeIndex && newActiveIndex >= 0 && newActiveIndex < images.length) {
            setActiveIndex(newActiveIndex)
            onSlideChange?.({activeIndex: newActiveIndex})
        }
    }, [activeIndex, images.length, onSlideChange])

    // Set up scroll listener for mobile carousel
    useEffect(() => {
        const container = scrollContainerRef.current
        if (!container) return

        updateScrollState()
        container.addEventListener('scroll', updateScrollState, {passive: true})
        return () => container.removeEventListener('scroll', updateScrollState)
    }, [updateScrollState])

    // Calculate scrollbar thumb width for mobile
    const scrollbarThumbWidth = images.length > 0 ? 100 / images.length : 100

    return (
        <Box position="relative">
            {/* Badges - positioned outside grid/carousel to not affect nth-of-type counting */}
            {/* Mobile: offset by -16px margin + 8px padding = -8px from edge */}
            <Box
                position="absolute"
                top={1}
                left={{base: '-12px', lg: 1}}
                zIndex={1}
            >
                <Badges badges={badges} />
            </Box>

            {/* Single container that switches between carousel (mobile) and grid (desktop) */}
            <Box
                ref={scrollContainerRef}
                sx={{
                    // Mobile: horizontal scroll carousel
                    display: 'flex',
                    gap: `${MOBILE_GAP}px`,
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    scrollBehavior: 'smooth',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    '&::-webkit-scrollbar': {display: 'none'},
                    // Mobile: each item is full width
                    '& > *': {
                        flexShrink: 0,
                        width: '100vw'
                    },
                    // Desktop (lg+): switch to CSS grid
                    '@media (min-width: 62em)': {
                        display: 'grid',
                        gridTemplateColumns: `repeat(${GRID_COLUMNS}, 1fr)`,
                        gap: `${DESKTOP_GAP}px`,
                        overflowX: 'visible',
                        // Desktop: every image spans 3 columns, i.e. 2 per row
                        '& > *': {
                            width: 'auto',
                            gridColumn: 'span 3'
                        },
                        ...(threeColumnTail && {
                            [`& > *:nth-of-type(n+${LEADING_IMAGES_COUNT + 1})`]: {
                                gridColumn: 'span 2'
                            }
                        })
                    }
                }}
                mx={{base: '-16px', lg: 0}}
            >
                {images.map((image, index) => (
                    <Box
                        key={`gallery-${index}`}
                        position="relative"
                    >
                        {image.isVideo ? (
                            <VideoGalleryItem
                                image={image}
                                isActiveSlide={activeIndex === index}
                                wrapperStyle={videoItemStyle}
                                elementStyle={videoElementStyle}
                                cursor={MAGNIFYING_GLASS_CURSOR}
                                onOpenModal={() => onImageClick(index)}
                            />
                        ) : (
                            <Button
                                as={Link}
                                variant="unstyled"
                                href="#"
                                cursor={MAGNIFYING_GLASS_CURSOR}
                                onClick={(e) => {
                                    e.preventDefault()
                                    onImageClick(index)
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault()
                                        onImageClick(index)
                                    }
                                }}
                                aria-label={`View larger image ${index + 1} of ${images.length}`}
                                display="block"
                                width="100%"
                                height="unset"
                            >
                                <ImageItem
                                    image={image}
                                    index={index}
                                    loadingStrategy={
                                        index < LEADING_IMAGES_COUNT ? 'eager' : loadingStrategy
                                    }
                                    getImageUrl={getImageUrl}
                                    isAboveFold={index < LEADING_IMAGES_COUNT}
                                    widths={
                                        threeColumnTail && index >= LEADING_IMAGES_COUNT
                                            ? IMAGE_WIDTHS.small
                                            : IMAGE_WIDTHS.large
                                    }
                                    imageProps={{
                                        width: '100%',
                                        ...imageProps
                                    }}
                                    imageContainerProps={{...imageContainerProps}}
                                />
                            </Button>
                        )}
                    </Box>
                ))}
            </Box>

            {/* Custom scrollbar for mobile - hidden on desktop */}
            {images.length > 1 && (
                <Box
                    display={{base: 'block', lg: 'none'}}
                    position="relative"
                    height="3px"
                    backgroundColor="transparent"
                    mx="-16px"
                >
                    <Box
                        position="absolute"
                        top={0}
                        left={`calc(${scrollProgress} * (100% - min(${scrollbarThumbWidth}%, 40px)))`}
                        width={`${scrollbarThumbWidth}%`}
                        maxWidth="40px"
                        height="3px"
                        backgroundColor="webBasic.600"
                        borderRadius="9999px"
                    />
                </Box>
            )}
        </Box>
    )
}

ResponsiveGallery.propTypes = {
    images: PropTypes.array.isRequired,
    badges: PropTypes.array,
    onImageClick: PropTypes.func.isRequired,
    onSlideChange: PropTypes.func.isRequired,
    getImageUrl: PropTypes.func.isRequired,
    loadingStrategy: PropTypes.oneOf(['lazy', 'eager']).isRequired,
    imageContainerProps: PropTypes.object,
    imageProps: PropTypes.object,
    videoItemStyle: PropTypes.object,
    videoElementStyle: PropTypes.object,
    threeColumnTail: PropTypes.bool
}

/**
 * Gift gallery component - desktop shows single image, mobile shows carousel
 * Uses CSS-based responsive switching for zero CLS
 */
const GiftGallery = ({images, badges, getImageUrl, loadingStrategy}) => {
    if (!images.length) return null

    return (
        <>
            {/* Desktop: single centered image */}
            <Flex
                display={{base: 'none', lg: 'flex'}}
                justifyContent="center"
                position="relative"
                width="100%"
            >
                <Box width="100%" maxWidth="600px" position="relative">
                    <Box position="absolute" top={1} left={1} zIndex={1}>
                        <Badges badges={badges} />
                    </Box>
                    <ImageItem
                        image={images[0]}
                        index={0}
                        loadingStrategy={loadingStrategy}
                        getImageUrl={getImageUrl}
                        imageContainerProps={{
                            ratio: { lg: 1 }
                        }}
                        imageProps={{
                            borderRadius: {lg: '16px'},
                            width: { lg: '100%' }
                        }}
                    />
                </Box>
            </Flex>
            {/* Mobile: carousel */}
            <Box display={{base: 'block', lg: 'none'}}>
                <ResponsiveGallery
                    images={images}
                    badges={badges}
                    onImageClick={() => {}}
                    onSlideChange={() => {}}
                    getImageUrl={getImageUrl}
                    loadingStrategy={loadingStrategy}
                    imageProps={{
                        borderRadius: {base: '10px'}
                    }}
                    imageContainerProps={{
                        mx: {base: '64px'}
                    }}
                />
            </Box>
        </>
    )
}

GiftGallery.propTypes = {
    images: PropTypes.array.isRequired,
    badges: PropTypes.array,
    getImageUrl: PropTypes.func.isRequired,
    loadingStrategy: PropTypes.oneOf(['lazy', 'eager']).isRequired
}

/**
 * Main ImageGallery component
 * Renders responsive image gallery with zoom modal functionality
 * Single DOM structure that uses CSS to switch between:
 * - Mobile: horizontal scroll carousel with custom scrollbar
 * - Desktop: CSS grid layout
 */
const ImageGallery = ({
    imageGroups = [],
    selectedVariationAttributes = {},
    size,
    lazy = false,
    badges = [],
    isGiftPage = false,
    videoSequencePositions = null
}) => {
    // Hooks
    const styles = useMultiStyleConfig('ImageGallery', {size})

    // State
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [selectedImageIndex, setSelectedImageIndex] = useState(0)

    // Memoized values
    const heroImageGroup = useMemo(
        () =>
            findImageGroupBy(imageGroups, {
                viewType: isGiftPage ? VIEW_TYPE_GIFT_CARD : VIEW_TYPE_LARGE,
                selectedVariationAttributes
            }),
        [imageGroups, selectedVariationAttributes]
    )

    const zoomImageGroup = useMemo(
        () =>
            findImageGroupBy(imageGroups, {
                viewType: VIEW_TYPE_ZOOM,
                selectedVariationAttributes
            }),
        [imageGroups, selectedVariationAttributes]
    )

    const heroImages = useMemo(() => heroImageGroup?.images || [], [heroImageGroup])
    const zoomImages = useMemo(
        () => zoomImageGroup?.images || heroImages,
        [zoomImageGroup, heroImages]
    )

    // Parse video items and their target positions once, then merge them into both
    // the gallery (hero) and modal (zoom) sequences so videos appear in both places.
    const {videoItems, videoPositions} = useMemo(() => {
        if (!videoSequencePositions || !heroImages.length) {
            return {videoItems: [], videoPositions: []}
        }

        // Parse positions (1-indexed) from string like "1,4"
        const positions = String(videoSequencePositions)
            .split(',')
            .map((p) => parseInt(p.trim(), 10))
            .filter((p) => !isNaN(p) && p >= 1)

        if (!positions.length) return {videoItems: [], videoPositions: []}

        // Find video group for the selected color
        const selectedColor = selectedVariationAttributes?.color
        const videoGroup = imageGroups.find((group) => {
            if (group.viewType !== VIEW_TYPE_VIDEO) return false
            if (!selectedColor) return true
            const colorAttr = group.variationAttributes?.find((a) => a.id === 'color')
            return colorAttr?.values?.some((v) => v.value === selectedColor)
        })

        if (!videoGroup?.images?.length) return {videoItems: [], videoPositions: []}

        const items = videoGroup.images.map((img) => ({
            isVideo: true,
            src: img.link,
            alt: img.alt,
            title: img.title
        }))

        return {videoItems: items, videoPositions: positions}
    }, [heroImages.length, videoSequencePositions, selectedVariationAttributes, imageGroups])

    const mergedImages = useMemo(
        () => insertVideosAtPositions(heroImages, videoItems, videoPositions),
        [heroImages, videoItems, videoPositions]
    )
    const mergedZoomImages = useMemo(
        () => insertVideosAtPositions(zoomImages, videoItems, videoPositions),
        [zoomImages, videoItems, videoPositions]
    )

    const loadingStrategy = useMemo(() => (lazy ? 'lazy' : 'eager'), [lazy])
    const {app: appConfig} = getConfig()
    const {site} = useMultiSite()
    const PRODUCT_IMGS_RESIZE_PARAMS_ENABLED = getSiteEnvVarValue(appConfig, 'PRODUCT_IMGS_RESIZE_PARAMS_ENABLED', site?.id) == 'true'
    const isThreeColumnEnabled = useThreeColumnTailEnabled()
    const threeColumnTail = hasThreeColumnTail(mergedImages.length, isThreeColumnEnabled)

    // Callback functions
    const getImageUrl = useCallback((image) => {
        const baseUrl = image.disBaseLink || image.link
        return PRODUCT_IMGS_RESIZE_PARAMS_ENABLED
        ? `${baseUrl}[?sw={width}&q=75&fmt=webp]`
        : baseUrl
    }, [])

    const handleImageClick = useCallback((imageIndex) => {
        setSelectedImageIndex(imageIndex)
        setIsModalOpen(true)
    }, [])

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false)
    }, [])

    const handleSlideChange = useCallback((data) => {
        setSelectedImageIndex(data.activeIndex)
    }, [])

    if (isGiftPage) {
        return (
            <Flex
                direction="column"
                alignItems="center"
                justifyContent="center"
                h={{base: 'auto', lg: '100%'}}
                minH={{base: 'auto', lg: 'calc(100vh - 80px)'}}
                bg="gray.200"
            >
                <Box {...styles.heroImageGroup} alignContent="center" w="100%">
                    <GiftGallery
                        images={heroImages}
                        badges={badges}
                        getImageUrl={getImageUrl}
                        loadingStrategy={loadingStrategy}
                    />
                </Box>
            </Flex>
        )
    }

    return (
        <Flex direction="column">
            <Box {...styles.heroImageGroup}>
                <ResponsiveGallery
                    images={mergedImages}
                    badges={badges}
                    onImageClick={handleImageClick}
                    onSlideChange={handleSlideChange}
                    getImageUrl={getImageUrl}
                    loadingStrategy={loadingStrategy}
                    videoItemStyle={styles.videoGalleryWrapper}
                    videoElementStyle={styles.videoGalleryElement}
                    threeColumnTail={threeColumnTail}
                />
            </Box>

            {/* Zoom modal for enlarged image viewing */}
            <ProductZoomModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                images={mergedZoomImages}
                thumbImages={mergedImages}
                initialImageIndex={selectedImageIndex}
                getImageUrl={getImageUrl}
            />
        </Flex>
    )
}

ImageGallery.propTypes = {
    imageGroups: PropTypes.array,
    selectedVariationAttributes: PropTypes.object,
    size: PropTypes.string,
    lazy: PropTypes.bool,
    badges: PropTypes.array,
    isGiftPage: PropTypes.bool,
    videoSequencePositions: PropTypes.string
}

export default ImageGallery
